import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Sparkles, Crown, Lock, ArrowUpRight, X, Zap, Check,
  Palette, Globe, BarChart3, Code, Shield, Truck, Tag,
  Gift, Rocket, Star, Loader2,
} from "lucide-react";
import { setFeatureUpgradeCallback } from "../api/client";
import api from "../api/client";
import { isFeatureModalSuppressed, suppressFeatureModalGlobal } from "../utils/featureModalState";
import AddonUpsellInline from "./AddonUpsellInline";

// Re-export for convenience
export { suppressFeatureModalGlobal };

// Features eligible for self-service trial
const TRIAL_ELIGIBLE = ["theme_forks", "funnels", "advanced_analytics", "custom_domain", "driver_app", "paid_themes", "ai_copywriting"];

// Feature metadata for upgrade prompts
const FEATURE_INFO = {
  theme_forks: {
    name: "Custom Theme Forks",
    icon: Palette,
    color: "from-purple-500 to-indigo-600",
    description: "Create unlimited custom themes by forking and customizing existing designs",
    benefits: [
      "Fork any theme and make it yours",
      "Full control over colors, fonts, and layouts",
      "Save multiple versions with backups",
      "Pull updates from base themes",
    ],
    plans: ["Professional", "Enterprise"],
  },
  funnels: {
    name: "Marketing Funnels",
    icon: Tag,
    color: "from-pink-500 to-rose-600",
    description: "Capture leads with exit-intent popups, timed offers, and conversion funnels",
    benefits: [
      "Exit-intent lead capture popups",
      "Timed promotional offers",
      "FOMO social proof notifications",
      "Full funnel analytics & conversion tracking",
    ],
    plans: ["Growth", "Professional", "Enterprise"],
  },
  advanced_analytics: {
    name: "Advanced Analytics",
    icon: BarChart3,
    color: "from-blue-500 to-cyan-600",
    description: "Deep insights into your business with cart recovery tracking and per-site analytics",
    benefits: [
      "Cart abandonment & recovery metrics",
      "Per-microsite performance dashboards",
      "Revenue trends & forecasting",
      "Booking source attribution",
    ],
    plans: ["Professional", "Enterprise"],
  },
  custom_domain: {
    name: "Custom Domains",
    icon: Globe,
    color: "from-emerald-500 to-teal-600",
    description: "Connect your own domain for a professional, branded customer experience",
    benefits: [
      "Use your own domain (e.g., www.yoursite.com)",
      "Free SSL certificate included",
      "DNS verification & management",
      "Multiple domains per site",
    ],
    plans: ["Professional", "Enterprise"],
  },
  api_access: {
    name: "API Access",
    icon: Code,
    color: "from-slate-600 to-slate-800",
    description: "Build custom integrations with full REST API access",
    benefits: [
      "Full REST API documentation",
      "Webhook integrations",
      "Custom automation workflows",
      "Developer support",
    ],
    plans: ["Enterprise"],
  },
  white_label: {
    name: "White Label",
    icon: Shield,
    color: "from-amber-500 to-orange-600",
    description: "Remove all platform branding for a fully white-labeled experience",
    benefits: [
      "Remove 'Powered by' branding",
      "Custom email sender domains",
      "Branded customer portal",
      "Full brand ownership",
    ],
    plans: ["Enterprise"],
    // One-click subscription add-on — shown on the gate so Pro-tier tenants
    // can unlock white-label without upgrading to Enterprise.
    addon: {
      slug: "white_label",
      name: "White-Label Branding",
      monthly_price: 29.00,
      min_plan_slug: "professional",
      description: "Unlock white-label for $29/mo. Available on Professional tier and above.",
    },
  },
  driver_app: {
    name: "Driver App",
    icon: Truck,
    color: "from-violet-500 to-purple-600",
    description: "Mobile app for drivers with route optimization and real-time updates",
    benefits: [
      "Native mobile app for drivers",
      "Turn-by-turn navigation",
      "Real-time delivery status updates",
      "Photo proof of delivery",
    ],
    plans: ["Professional", "Enterprise"],
  },
  paid_themes: {
    name: "Premium Themes",
    icon: Crown,
    color: "from-yellow-500 to-amber-600",
    description: "Access our library of professionally designed premium themes",
    benefits: [
      "Exclusive premium theme designs",
      "Regular new theme releases",
      "Priority theme support",
      "Early access to new features",
    ],
    plans: ["Professional", "Enterprise"],
  },
  ai_copywriting: {
    name: "AI Copywriting",
    icon: Sparkles,
    color: "from-purple-500 to-pink-600",
    description: "AI-powered content generation for product descriptions, SEO, and marketing copy",
    benefits: [
      "One-click product descriptions & SEO",
      "AI image alt text generation",
      "Rewrite text with custom instructions",
      "Monthly AI credits based on your plan",
    ],
    plans: ["Starter", "Growth", "Professional", "Enterprise"],
  },
};

// Default fallback for unknown features
const DEFAULT_FEATURE = {
  name: "Premium Feature",
  icon: Zap,
  color: "from-indigo-500 to-purple-600",
  description: "Unlock this powerful feature to grow your business",
  benefits: [
    "Boost your productivity",
    "Stand out from competitors",
    "Grow your revenue",
    "Premium support included",
  ],
  plans: ["Growth", "Professional", "Enterprise"],
};

// Context for the modal
const FeatureUpgradeContext = createContext({
  showUpgradeModal: () => {},
  hideUpgradeModal: () => {},
  setSuppressModal: () => {},
});

export function useFeatureUpgrade() {
  return useContext(FeatureUpgradeContext);
}

// Helper to check if feature was recently dismissed
function wasRecentlyDismissed(feature) {
  try {
    const dismissed = JSON.parse(localStorage.getItem("feature_upgrade_dismissed") || "{}");
    const dismissedAt = dismissed[feature];
    if (!dismissedAt) return false;
    
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    return dismissedAt > thirtyDaysAgo;
  } catch {
    return false;
  }
}

// Helper to mark feature as dismissed
function markDismissed(feature) {
  try {
    const dismissed = JSON.parse(localStorage.getItem("feature_upgrade_dismissed") || "{}");
    dismissed[feature] = Date.now();
    localStorage.setItem("feature_upgrade_dismissed", JSON.stringify(dismissed));
  } catch {
    // ignore storage errors
  }
}

export function FeatureUpgradeProvider({ children }) {
  const [modalState, setModalState] = useState({ open: false, feature: null, message: null });
  const [startingTrial, setStartingTrial] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const navigate = useNavigate();
  
  // Function to set suppression - updates both ref and triggers re-render
  const setSuppressModal = useCallback((value) => {
    suppressFeatureModalGlobal(value);
  }, []);

  const showUpgradeModal = useCallback((feature, message = null) => {
    // Don't show if suppressed (background loading) - check shared state
    if (isFeatureModalSuppressed()) {
      console.log(`Upgrade modal for ${feature} suppressed (background loading)`);
      return;
    }
    // Don't show if dismissed within last 30 days
    if (wasRecentlyDismissed(feature)) {
      console.log(`Upgrade modal for ${feature} suppressed (dismissed within 30 days)`);
      return;
    }
    setDontShowAgain(false); // Reset checkbox state for new modal
    setModalState({ open: true, feature, message });
  }, []);

  const hideUpgradeModal = useCallback(() => {
    // Mark as dismissed only if user checked "Don't show again"
    if (dontShowAgain && modalState.feature) {
      markDismissed(modalState.feature);
    }
    setModalState({ open: false, feature: null, message: null });
    setStartingTrial(false);
    setDontShowAgain(false);
  }, [modalState.feature, dontShowAgain]);

  // Register the callback with the API client on mount
  useEffect(() => {
    setFeatureUpgradeCallback(showUpgradeModal);
    return () => setFeatureUpgradeCallback(null);
  }, [showUpgradeModal]);

  const handleUpgrade = () => {
    hideUpgradeModal();
    navigate(`/admin/settings?tab=billing&highlight=${modalState.feature}`);
  };

  const handleViewPlans = () => {
    hideUpgradeModal();
    navigate("/admin/upgrade");
  };

  const handleStartTrial = async () => {
    if (!modalState.feature || startingTrial) return;
    
    setStartingTrial(true);
    try {
      const res = await api.post(`/api/tenant/features/start-trial/${modalState.feature}`);
      toast.success(res.data.message || "Trial activated!", {
        description: `Your ${res.data.feature_name} trial expires on ${new Date(res.data.expires_at).toLocaleDateString()}.`,
      });
      hideUpgradeModal();
      // Reload the page to refresh feature access
      window.location.reload();
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not start trial. Please try again.";
      toast.error("Trial unavailable", { description: detail });
      setStartingTrial(false);
    }
  };

  const featureInfo = modalState.feature 
    ? (FEATURE_INFO[modalState.feature] || { ...DEFAULT_FEATURE, name: modalState.feature.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) })
    : DEFAULT_FEATURE;
  
  const Icon = featureInfo.icon;
  const isTrialEligible = modalState.feature && TRIAL_ELIGIBLE.includes(modalState.feature);

  return (
    <FeatureUpgradeContext.Provider value={{ showUpgradeModal, hideUpgradeModal, setSuppressModal }}>
      {children}

      {/* Upgrade Modal */}
      {modalState.open && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={hideUpgradeModal}
          data-testid="feature-upgrade-modal-backdrop"
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
            data-testid="feature-upgrade-modal"
          >
            {/* Header with gradient */}
            <div className={`relative px-6 pt-8 pb-16 bg-gradient-to-br ${featureInfo.color}`}>
              <button 
                onClick={hideUpgradeModal}
                className="absolute top-4 right-4 h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
                data-testid="feature-upgrade-modal-close"
              >
                <X className="h-4 w-4 text-white" />
              </button>
              
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                  <Icon className="h-7 w-7 text-white" />
                </div>
                <div className="min-w-0">
                  <Badge className="mb-1 bg-white/20 text-white border-0 text-[10px]">
                    <Lock className="h-2.5 w-2.5 mr-1" /> Premium Feature
                  </Badge>
                  <h2 className="text-xl font-bold text-white truncate">{featureInfo.name}</h2>
                </div>
              </div>
              
              {/* Decorative curve */}
              <div className="absolute -bottom-1 left-0 right-0 h-8 bg-white rounded-t-3xl" />
            </div>

            {/* Content */}
            <div className="px-6 pb-6 pt-2">
              <p className="text-slate-600 text-sm mb-4">
                {modalState.message || featureInfo.description}
              </p>

              {/* Benefits */}
              <div className="space-y-2 mb-6">
                {featureInfo.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-emerald-600" />
                    </div>
                    <span className="text-sm text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Available in plans */}
              <div className="mb-6 p-3 rounded-xl bg-slate-50">
                <p className="text-[10px] font-semibold uppercase text-slate-400 mb-2">Available in</p>
                <div className="flex flex-wrap gap-1.5">
                  {featureInfo.plans.map(plan => (
                    <Badge 
                      key={plan} 
                      variant="outline" 
                      className="text-xs bg-white border-slate-200"
                    >
                      <Star className="h-3 w-3 mr-1 text-amber-500" /> {plan}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="space-y-3">
                {/* Trial Button - prominent if eligible */}
                {isTrialEligible && (
                  <Button 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white border-0 h-11"
                    onClick={handleStartTrial}
                    disabled={startingTrial}
                    data-testid="feature-start-trial"
                  >
                    {startingTrial ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        Activating...
                      </>
                    ) : (
                      <>
                        <Gift className="h-4 w-4 mr-1.5" />
                        Try Free for 7 Days
                      </>
                    )}
                  </Button>
                )}

                {/* One-click add-on — shown when the feature has an addon
                    pathway so tenants can unlock without upgrading plans. */}
                {featureInfo.addon && (
                  <AddonUpsellInline
                    addonSlug={featureInfo.addon.slug}
                    addonName={featureInfo.addon.name}
                    monthlyPrice={featureInfo.addon.monthly_price}
                    description={featureInfo.addon.description}
                    data-testid={`feature-addon-upsell-${modalState.feature}`}
                  />
                )}
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={handleViewPlans}
                    data-testid="feature-upgrade-view-plans"
                  >
                    Compare Plans
                  </Button>
                  <Button 
                    className={`flex-1 bg-gradient-to-r ${featureInfo.color} hover:opacity-90 text-white border-0`}
                    onClick={handleUpgrade}
                    data-testid="feature-upgrade-button"
                  >
                    <Rocket className="h-4 w-4 mr-1.5" />
                    Upgrade Now
                  </Button>
                </div>
              </div>

              {/* Trial hint - only show if NOT trial eligible (since button handles it) */}
              {!isTrialEligible && (
                <p className="text-center text-[11px] text-slate-400 mt-4">
                  <Gift className="h-3 w-3 inline mr-1" />
                  Contact support for trial access to this feature
                </p>
              )}
              
              {/* Don't show again checkbox */}
              <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-slate-100">
                <Checkbox
                  id="dont-show-again"
                  checked={dontShowAgain}
                  onCheckedChange={setDontShowAgain}
                  className="h-4 w-4"
                  data-testid="dont-show-again-checkbox"
                />
                <label 
                  htmlFor="dont-show-again" 
                  className="text-[11px] text-slate-400 cursor-pointer select-none"
                >
                  Don't show this again for 30 days
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </FeatureUpgradeContext.Provider>
  );
}

// Export feature info for use in other components
export { FEATURE_INFO };
